import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoveHorizontal, LayoutGrid, Palette, Send, Check } from 'lucide-react';
import { IS_DEMO, PERSONAS, resolvePersona, rememberPersona, switchPersona, type PersonaId } from '@/lib/demo';
import { trackDemo } from '@/lib/demoTracking';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Demo-only first-run guide. Step 1 confirms which version to show (guessed
 * from the Telegram account's first name), then four short cards explain how
 * to move around, the command menu, the customizer and how to send the result
 * to the team. Shown once per device; `?tour=1` shows it again.
 */

const DONE_KEY = 'demo_onboarded_v1';
/** Step to resume at after the persona switch reloads the page */
const RESUME_KEY = 'demo_tour_resume';

const COPY = {
  es: {
    hi: (n: string) => (n ? `Hola, ${n}` : 'Hola'),
    welcome: 'Así se vería tu Mini App',
    pick: '¿Qué versión quieres ver?',
    detected: 'La elegimos según tu cuenta de Telegram. Puedes cambiarla.',
    chooseOne: 'Elige la que más se parezca a tu marca.',
    persona: { mujer: 'Creadora', hombre: 'Creador' } as Record<PersonaId, string>,
    personaHint: { mujer: 'Suave, claro, rosa', hombre: 'Oscuro, sobrio, acero' } as Record<PersonaId, string>,
    steps: [
      { title: 'Desliza', body: 'Izquierda y derecha para pasar por Inicio, Galería, Canales, Redes y Contacto. Así lo verán tus fans.' },
      { title: 'Menú de comandos', body: 'El botón redondo de abajo abre /vip, /sub, /wishlist y el resto, igual que en el bot.' },
      { title: 'Hazla tuya', body: 'Con el botón de paleta, arriba a la derecha, cambias colores, letra, fondo, tu nombre, precios y redes. Solo lo ves tú.' },
      { title: '¿Te gustó?', body: 'En Personalizar → Exportar envías tu diseño al equipo, o escríbele al asesor en el chat y la montamos.' },
    ],
    next: 'Siguiente', start: 'Empezar', skip: 'Saltar', continue: 'Continuar',
  },
  en: {
    hi: (n: string) => (n ? `Hi, ${n}` : 'Hi'),
    welcome: 'This is what your Mini App could look like',
    pick: 'Which version do you want to see?',
    detected: 'We picked it from your Telegram account. You can change it.',
    chooseOne: 'Pick the one closest to your brand.',
    persona: { mujer: 'Female creator', hombre: 'Male creator' } as Record<PersonaId, string>,
    personaHint: { mujer: 'Soft, light, pink', hombre: 'Dark, sober, steel' } as Record<PersonaId, string>,
    steps: [
      { title: 'Swipe', body: 'Left and right to go through Home, Gallery, Channels, Networks and Contact. This is what your fans see.' },
      { title: 'Command menu', body: 'The round button at the bottom opens /vip, /sub, /wishlist and the rest, just like in the bot.' },
      { title: 'Make it yours', body: 'The palette button, top right, changes colours, type, background, your name, prices and networks. Only you see it.' },
      { title: 'Like it?', body: 'In Customize → Export you send your design to the team, or message the advisor in the chat and we build it.' },
    ],
    next: 'Next', start: 'Start', skip: 'Skip', continue: 'Continue',
  },
};

const ICONS = [MoveHorizontal, LayoutGrid, Palette, Send];

function shouldShow(): { show: boolean; step: number } {
  if (!IS_DEMO || typeof window === 'undefined') return { show: false, step: 0 };
  let resume: string | null = null;
  try { resume = sessionStorage.getItem(RESUME_KEY); sessionStorage.removeItem(RESUME_KEY); } catch { /* private mode */ }
  if (resume) return { show: true, step: Number(resume) || 1 };
  if (new URLSearchParams(window.location.search).get('tour') === '1') return { show: true, step: 0 };
  try { return { show: !localStorage.getItem(DONE_KEY), step: 0 }; } catch { return { show: true, step: 0 }; }
}

export const DemoOnboarding: React.FC<{ lang: 'es' | 'en'; ready: boolean }> = ({ lang, ready }) => {
  const [{ show, step: firstStep }] = useState(shouldShow);
  const [open, setOpen] = useState(show);
  const [step, setStep] = useState(firstStep);
  const [{ id: current, source }] = useState(resolvePersona);
  const [picked, setPicked] = useState<PersonaId>(current);
  const { hapticFeedback } = useTelegram();
  const t = COPY[lang] ?? COPY.es;
  const firstName = (window.Telegram?.WebApp?.initDataUnsafe?.user?.first_name || '').trim().split(/\s+/)[0] || '';
  const total = t.steps.length + 1;

  if (!IS_DEMO || !open || !ready) return null;

  const finish = (skipped: boolean) => {
    hapticFeedback.impact('light');
    try { localStorage.setItem(DONE_KEY, '1'); } catch { /* private mode */ }
    trackDemo('onboarding', { step, skipped, persona: current, detected: source === 'telegram' });
    setOpen(false);
  };

  const next = () => {
    hapticFeedback.selection();
    if (step === 0) {
      rememberPersona(picked);
      if (picked !== current) {
        trackDemo('persona', { persona: picked, from: 'onboarding' });
        try { sessionStorage.setItem(RESUME_KEY, '1'); } catch { /* private mode */ }
        switchPersona(picked);
        return;
      }
    }
    if (step >= total - 1) finish(false);
    else setStep(step + 1);
  };

  const card = step > 0 ? t.steps[step - 1] : null;
  const Icon = step > 0 ? ICONS[step - 1] : null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      role="dialog" aria-modal="true" aria-label={t.welcome}
      style={{
        position: 'absolute', inset: 0, zIndex: 90, display: 'flex', alignItems: 'flex-end',
        background: 'rgb(0 0 0 / 0.45)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        fontFamily: 'var(--ff-body)', color: 'var(--c-text)',
      }}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 30 }}
        style={{
          width: '100%', margin: 12, padding: '22px 20px 18px', background: 'var(--c-sheet)',
          border: '1px solid var(--c-line)', borderRadius: 'calc(22px * var(--radius-scale, 1))',
          boxShadow: '0 30px 60px rgb(0 0 0 / 0.35)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 5 }} aria-hidden>
            {Array.from({ length: total }).map((_, i) => (
              <span key={i} style={{ width: i === step ? 18 : 6, height: 6, borderRadius: 'var(--radius-pill, 999px)', background: i <= step ? 'var(--c-crimson)' : 'var(--c-line)', transition: 'all .3s' }} />
            ))}
          </div>
          <button type="button" onClick={() => finish(true)} style={{ background: 'none', border: 'none', color: 'var(--c-text-dim)', font: 'inherit', fontSize: 13, cursor: 'pointer', padding: 4 }}>
            {t.skip}
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
            {step === 0 ? (
              <>
                <div style={{ fontSize: 'var(--fs-micro)', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--c-crimson)' }}>
                  {t.hi(firstName)}
                </div>
                <h2 style={{ fontFamily: 'var(--ff-display)', fontSize: 'var(--fs-heading)', lineHeight: 1, margin: '8px 0 6px', textTransform: 'uppercase' }}>{t.welcome}</h2>
                <p style={{ margin: '0 0 14px', fontSize: 14, lineHeight: 1.45, color: 'var(--c-text-dim)' }}>
                  {t.pick} {source === 'telegram' ? t.detected : t.chooseOne}
                </p>
                <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {(Object.keys(PERSONAS) as PersonaId[]).map((id) => {
                    const on = picked === id;
                    return (
                      <button
                        key={id} type="button" role="radio" aria-checked={on}
                        onClick={() => { hapticFeedback.selection(); setPicked(id); }}
                        style={{
                          position: 'relative', textAlign: 'left', padding: 0, overflow: 'hidden', cursor: 'pointer',
                          borderRadius: 'calc(16px * var(--radius-scale, 1))', font: 'inherit', color: '#fff',
                          border: `2px solid ${on ? 'var(--c-crimson)' : 'var(--c-line)'}`, background: '#000',
                        }}
                      >
                        <img src={`/demo-photos/${id}/01.jpg`} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block', opacity: on ? 1 : 0.6, transition: 'opacity .2s' }} />
                        <div style={{ position: 'absolute', inset: 'auto 0 0 0', padding: '24px 10px 9px', background: 'linear-gradient(to top, rgb(0 0 0 / 0.8), transparent)' }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{t.persona[id]}</div>
                          <div style={{ fontSize: 11, opacity: 0.8 }}>{t.personaHint[id]}</div>
                        </div>
                        {on && (
                          <span style={{ position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: '50%', background: 'var(--c-crimson)', color: 'var(--c-on-crimson)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={14} strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                {Icon && (
                  <span style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 'calc(14px * var(--radius-scale, 1))', background: 'rgb(var(--c-crimson-rgb) / 0.14)', color: 'var(--c-crimson)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={22} />
                  </span>
                )}
                <div>
                  <h2 style={{ fontFamily: 'var(--ff-display)', fontSize: 'var(--fs-heading)', lineHeight: 1, margin: '2px 0 8px', textTransform: 'uppercase' }}>{card?.title}</h2>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--c-text-dim)' }}>{card?.body}</p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <button
          type="button" onClick={next}
          style={{
            width: '100%', marginTop: 18, padding: '13px 16px', border: 'none', cursor: 'pointer',
            borderRadius: 'var(--radius-pill, 999px)', background: 'var(--c-crimson)', color: 'var(--c-on-crimson)',
            font: 'inherit', fontWeight: 700, fontSize: 15,
          }}
        >
          {step === 0 ? t.continue : step >= total - 1 ? t.start : t.next}
        </button>
      </motion.div>
    </motion.div>
  );
};

export default DemoOnboarding;
